import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { api, ApiError } from '@/lib/api'
import type { AILinkInventoryItem } from '@/types/api'

type Props = {
  projectId: string
  canEdit: boolean
  refreshKey: number
  tabActive?: boolean
  onContentUpdated: () => void
}

function sourceLabel(source: string) {
  if (source === 'instruction') return 'Инструкция'
  if (source === 'knowledge_base') return 'База знаний'
  if (source === 'system_prompt') return 'Системный промпт'
  return source
}

export function AiLinkReplaceTab({
  projectId,
  canEdit,
  refreshKey,
  tabActive = true,
  onContentUpdated,
}: Props) {
  const [links, setLinks] = useState<AILinkInventoryItem[]>([])
  const [totalOccurrences, setTotalOccurrences] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [replaceTo, setReplaceTo] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const data = await api.aiLinksInventory(projectId)
      setLinks(Array.isArray(data.links) ? data.links : [])
      setTotalOccurrences(data.total_occurrences ?? 0)
      setReplaceTo({})
      setExpanded({})
    } catch (e) {
      setLinks([])
      setTotalOccurrences(0)
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить ссылки',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    if (!tabActive) return
    void load()
  }, [load, refreshKey, tabActive])

  const pendingReplacements = useMemo(() => {
    return links
      .map((item) => {
        const to = (replaceTo[item.link] ?? '').trim()
        if (!to || to === item.link) return null
        return { from: item.link, to, count: item.occurrence_count }
      })
      .filter(Boolean) as { from: string; to: string; count: number }[]
  }, [links, replaceTo])

  const applyReplacements = () => {
    if (!canEdit || pendingReplacements.length === 0) return

    const lines = pendingReplacements
      .map((r) => `• «${r.from}» → «${r.to}» (${r.count} вхождений)`)
      .join('\n')

    openConfirmModal({
      title: 'Подтвердите замену ссылок',
      children: (
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {lines}
          {'\n\n'}
          Замена точная по тексту. Отменить автоматически нельзя.
        </Text>
      ),
      labels: { confirm: 'Заменить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setSaving(true)
        try {
          const res = await api.aiLinksReplace(projectId, {
            replacements: pendingReplacements.map(({ from, to }) => ({ from, to })),
          })
          showNotification({
            title: 'Ссылки заменены',
            message: `Обновлено вхождений: ${res.links_substituted}. Инструкций: ${res.instructions_updated}, БЗ: ${res.knowledge_base_updated}.`,
            color: 'green',
          })
          showNotification({
            title: 'Синхронизация с ассистентом',
            message:
              'Выполните «Синхронизировать ассистента» на вкладке «Основные» и при необходимости обновите векторное хранилище / базу знаний на шлюзе.',
            color: 'blue',
            autoClose: 12000,
          })
          onContentUpdated()
          await load()
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Замена не выполнена',
            color: 'red',
          })
        } finally {
          setSaving(false)
        }
      },
    })
  }

  return (
    <Stack gap="md">
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Text fw={600} mb={4}>
              Замена ссылок
            </Text>
            <Text size="xs" c="dimmed">
              Уникальные ссылки из инструкций, базы знаний и системного промпта. «/path» и «domain.ru/path»
              считаются разными. Одинаковая ссылка в нескольких местах — одна строка со списком вхождений.
            </Text>
          </div>
          <Group gap="xs">
            <Badge variant="light">{links.length} ссылок</Badge>
            <Badge variant="light" color="gray">
              {totalOccurrences} вхождений
            </Badge>
            <Button size="sm" variant="default" loading={loading} onClick={() => void load()}>
              Обновить
            </Button>
          </Group>
        </Group>
      </Card>

      {!loading && links.length === 0 ? (
        <Text size="sm" c="dimmed">
          Ссылок не найдено. Добавьте URL в инструкции или базу знаний.
        </Text>
      ) : null}

      <div className="table-wrap">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Текущая ссылка</Table.Th>
              <Table.Th style={{ width: '36%' }}>Заменить на</Table.Th>
              <Table.Th>Вхождения</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text size="sm" c="dimmed">
                    Загрузка…
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              links.map((item) => {
                const open = expanded[item.link] ?? false
                const placesKey = item.link
                return (
                  <Table.Tr key={placesKey}>
                    <Table.Td>
                      <Text size="sm" className="mono" style={{ wordBreak: 'break-all' }}>
                        {item.link}
                      </Text>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        mt={4}
                        onClick={() =>
                          setExpanded((prev) => ({ ...prev, [item.link]: !open }))
                        }
                      >
                        {open ? 'Скрыть места' : `Где встречается (${item.occurrence_count})`}
                      </Button>
                      <Collapse in={open}>
                        <Stack gap={6} mt="xs">
                          {item.occurrences.map((o, idx) => (
                            <Text key={`${o.entity_id}-${o.field}-${idx}`} size="xs" c="dimmed">
                              <Text span fw={500} c="var(--mantine-color-text)">
                                {o.place_label}
                              </Text>
                              {' · '}
                              {sourceLabel(o.source)}, {o.field_label}
                              {o.snippet ? (
                                <>
                                  <br />
                                  <Text span fs="italic">
                                    {o.snippet}
                                  </Text>
                                </>
                              ) : null}
                            </Text>
                          ))}
                        </Stack>
                      </Collapse>
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        className="mono"
                        placeholder="Новая ссылка"
                        value={replaceTo[item.link] ?? ''}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setReplaceTo((prev) => ({
                            ...prev,
                            [item.link]: e.currentTarget.value,
                          }))
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="outline">
                        {item.occurrence_count}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                )
              })
            )}
          </Table.Tbody>
        </Table>
      </div>

      {canEdit && pendingReplacements.length > 0 ? (
        <Group justify="flex-end">
          <Button loading={saving} onClick={applyReplacements}>
            Заменить выбранные ({pendingReplacements.length})
          </Button>
        </Group>
      ) : null}
    </Stack>
  )
}
